<template>
	<div class="p-4 bg-white rounded-lg shadow-md group">
		<p class="font-serif text-base leading-6">{{ option.name }}</p>

		<div class="flex items-stretch gap-4 mt-4">
			<div
				class="relative flex-none w-20 min-h-20 min-[500px]:w-30 min-[500px]:min-h-30 self-stretch overflow-hidden rounded"
			>
				<Image
					:url="option.image"
					:fallback="`${baseUrl}assets/headers/Hamamruimte_vriendinnen.webp`"
					:width="240"
					:height="240"
					:alt="option.name ?? 'Hamam ruimte'"
					class="absolute inset-0 w-full h-full rounded object-cover"
				/>
				<span
					v-if="option.sticker?.visible"
					class="absolute top-0 inset-x-0 px-1.5 py-0.5 text-center font-nexus-sans text-xs font-normal leading-4 tracking-sticker lowercase [font-variant-caps:all-small-caps]"
					:style="stickerStyle"
				>
					{{ stickerText }}
				</span>
			</div>

			<div class="flex flex-col flex-1 gap-1">
				<!-- The bullet glyphs sit right of center in their viewBox; pull them left so they
					align with the other icons, with margin-right compensating so every text starts at the same x -->
				<ul
					v-if="option.usps?.length"
					class="flex flex-col gap-1 [&_.icon-bullet-circle]:-ml-[7px] [&_.icon-bullet-circle]:mr-[7px] [&_.icon-bullet-point]:-ml-2 [&_.icon-bullet-point]:mr-2"
				>
					<li v-for="(usp, index) in option.usps" :key="index" class="flex items-start gap-1">
						<SvgIcon
							:name="usp.icon"
							class="size-4 mt-0.5 flex-none"
							:style="{ color: usp.color?.hex ?? 'var(--color-gold-500)' }"
						/>
						<!-- eslint-disable-next-line vue/no-v-html -- description is managed rich text from the DMP -->
						<span class="text-xs font-medium leading-4 text-neutral-800 [&_p]:inline" v-html="usp.description" />
					</li>
				</ul>

				<button
					type="button"
					:disabled="props.loading"
					class="text-xs font-medium underline underline-offset-2 text-neutral-950 disabled:text-neutral-300 pt-1 self-start"
					@click="emit('details', selected)"
				>
					{{ t('more_info') }}
				</button>

				<div class="flex flex-wrap items-center gap-2 mt-auto pt-2">
					<PriceLine
						v-if="selected?.price_data"
						:price-data="selected?.price_data"
						:selected-date
						hide-suffix
						highlight-from-price
						class="text-neutral-950 [&_.price-from]:text-sm [&_.price-prefix]:text-sm [&_.price-amount]:text-xl [&_.price-amount]:font-medium"
					/>
					<button type="button" class="btn-primary h-8 px-5 text-xs ml-auto" @click="onBook">
						{{ t('choose') }}
					</button>
				</div>
			</div>
		</div>

		<ChoiceModal
			v-if="showChoiceModal && option.choices"
			:choices="option.choices"
			:selected-date
			@close="showChoiceModal = false"
			@book="onBookChoice"
		/>
	</div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Buyable, BuyableChoice } from '../api/resources';
import ChoiceModal from './ChoiceModal.vue';
import Image from "./Image.vue";
import PriceLine from "../components/PriceLine.vue";
import SvgIcon from './SvgIcon.vue';

export type Props = {
  option: Buyable;
  loading: boolean;
  selectedDate: string;
};

const { t } = useI18n();
const props = defineProps<Props>();

const emit = defineEmits<{
  (event: 'details', selected: Buyable | BuyableChoice): void;
  (event: 'book', selected: { reservation_flow_url: string }): void;
}>();

const selected = ref();

const showChoiceModal = ref(false);

const baseUrl = import.meta.env.BASE_URL;

// The sticker is plain text in the design; strip the rich text markup from the DMP.
// DOMParser creates an inert document, so nothing in the markup executes or loads.
const stickerText = computed(
  () =>
    new DOMParser().parseFromString(props.option.sticker?.text ?? '', 'text/html').body.textContent?.trim() ?? '',
);

const stickerStyle = computed(() => {
  const hex = props.option.sticker?.color?.hex ?? 'var(--color-gold-500)';

  return {
    backgroundColor: hex,
    color: isLightColor(props.option.sticker?.color?.hex) ? 'var(--color-neutral-950)' : '#ffffff',
  };
});

// Light sticker backgrounds (e.g. white) need dark text to stay readable
const isLightColor = (hex?: string | null) => {
  if (!hex) return false;

  const [r, g, b] = [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));

  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.7;
};

onMounted(() => {
  if (props.option.choices && props.option.choices.length > 0) {
    selected.value = props.option.choices[0];
  } else {
    selected.value = props.option;
  }
});

const onBook = () => {
  if (props.option.choices && props.option.choices.length > 0) {
    showChoiceModal.value = true;
    return;
  }

  emit('book', selected.value);
};

const onBookChoice = (choice: BuyableChoice) => {
  showChoiceModal.value = false;
  selected.value = choice;
  emit('book', choice);
};
</script>
